import { AxiosError, AxiosResponse } from 'axios'
import Results, { Result } from '@/hexapla-core/monads/result'

// Module Interface
export interface Response {
  fromPromise: <Res>(p: Promise<AxiosResponse<Res>>) => Promise<Res>
  result: <Res, E>(p: Promise<AxiosResponse<Res>>) => Promise<Result<Res, unknown> | Result<unknown, E>>
}

/**
 * A failed API call.
 *
 * `message` is always safe to show a person: the API's own error text when it
 * sent one, otherwise a sentence for the status. Callers display it directly,
 * so it must never be a raw body — a crashed request returns an HTML error page
 * (or, in development, a debug page with a stack trace and query), and that
 * used to land verbatim in the flash message.
 *
 * `status` and `body` let a caller branch on what actually happened. `status`
 * is undefined when no response arrived at all (network failure, CORS
 * rejection, API unreachable).
 */
export class ApiError extends Error {
  readonly status?: number
  readonly body?: unknown

  constructor(message: string, status?: number, body?: unknown) {
    super(message)
    Object.setPrototypeOf(this, ApiError.prototype)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

// Longer than any sentence the API writes; anything past it is a page, not a message.
const MAX_MESSAGE_LENGTH = 300

const isReadableText = (text: string): boolean =>
  text.length > 0 && text.length <= MAX_MESSAGE_LENGTH && !/^\s*</.test(text)

const humanize = (key: string): string => key.replace(/_/g, ' ')

/**
 * Flattens a changeset error map (`{ field: ["can't be blank"] }`, nested for
 * associations) into "field can't be blank" sentences.
 */
const changesetErrors = (errors: unknown, path: string[] = []): string[] => {
  if (typeof errors === 'string') return [[...path.map(humanize), errors].join(' ')]
  if (Array.isArray(errors)) return errors.flatMap((e) => changesetErrors(e, path))
  if (errors && typeof errors === 'object') {
    return Object.entries(errors).flatMap(([key, value]) => changesetErrors(value, [...path, key]))
  }
  return []
}

/**
 * The human-readable message in an API error body, if there is one.
 *
 * A rejected write answers `{ error: "Failed to create lemma", details: {…} }`
 * (older endpoints use `errors`). The summary alone told nobody what to fix, so
 * the field errors follow it: "Failed to create lemma: attestations translator
 * id can't be blank".
 */
export const messageFromBody = (body: unknown): string | undefined => {
  if (typeof body === 'string') return isReadableText(body.trim()) ? body.trim() : undefined
  if (!body || typeof body !== 'object') return undefined

  const { error, message, reason, errors, details } = body as Record<string, unknown>
  const text = [error, message, reason].find((v): v is string => typeof v === 'string' && isReadableText(v))
  const fields = [...changesetErrors(details), ...changesetErrors(errors)].slice(0, 3).join('; ')

  if (text && fields) return `${text}: ${fields}`
  return text || fields || undefined
}

/** A sentence for a failure the API did not explain. */
export const statusMessage = (status?: number): string => {
  if (status === undefined) return 'Could not reach the server. Check your connection and try again.'
  if (status === 401) return 'Your session has ended. Please sign in again.'
  if (status === 403) return 'You do not have permission to do that.'
  if (status === 404) return 'That could not be found.'
  if (status === 413) return 'That upload is too large.'
  if (status === 422) return 'Some of the information was not accepted.'
  if (status >= 500) return 'The server ran into a problem. Please try again.'
  return 'The request failed. Please try again.'
}

/** The human-readable message the API put in an error, if there is one. */
export const serverMessage = (error: unknown): string | undefined =>
  error instanceof ApiError ? messageFromBody(error.body) : undefined

export const fromPromise = <Res>(p: Promise<AxiosResponse<Res>>): Promise<Res> =>
  p
    .then(({ data }: AxiosResponse) => Promise.resolve(data))
    .catch(({ response }: AxiosError) => {
      const message = messageFromBody(response?.data) || statusMessage(response?.status)
      return Promise.reject(new ApiError(message, response?.status, response?.data))
    })

export const result = async <Res, E>(p: Promise<AxiosResponse<Res>>): Promise<Result<Res, unknown> | Result<unknown, E>> => {
  return await fromPromise(p)
    .then((data: Res) => Results.newOk<Res>(data))
    .catch((error: E) => Results.newError<E>(error))
}

export default <Response>{ fromPromise, result }
